import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch } from '@tanstack/react-router';
import PageWithHeadline from '@/components/layout/PageWithHeadline';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { SearchBox, SelectDropdown, Toggle, PrimaryButton, ErrorBanner } from '@/components';
import { FloatingActionBar, FloatingActionButton } from '@/components';
import { Check } from 'lucide-react';
import Pagination from '@/components/tables/Pagination';
import useDebounce from '@/utils/useDebounce';
import { useUser } from '@/app/providers';
import { hasFeature } from '@/domain/authentication';
import { TranslationsTable, ImportResultModal } from './components';
import {
  useLanguages,
  useTranslations,
  useUpdateTranslation,
  useExportTranslations,
  useImportTranslations,
  useClearNewEntries,
} from './hooks';
import type { TranslationQueryParams, ImportResult } from './types';

interface TranslationsSearchParams {
  language_id?: number;
  page?: number;
  page_size?: number;
  search?: string;
  new_only?: boolean;
}

function setSearchParams(params: TranslationsSearchParams) {
  const url = new URL(window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });

  try {
    window.history.replaceState({}, '', url.toString());
  } catch (error) {
    console.error('Failed to update URL search params:', error);
  }
}

export function TranslationsPage() {
  const { t } = useTranslation();
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const searchParams = useSearch({ from: '/_app/translations' });

  const [searchInput, setSearchInput] = useState(searchParams.search || '');
  const [newOnly, setNewOnly] = useState(searchParams.new_only || false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchInput);

  const { data: languages, isLoading: isLoadingLanguages } = useLanguages();

  const [selectedLanguageId, setSelectedLanguageId] = useState<number>(
    searchParams.language_id || 0
  );
  const [hasUserSelectedLanguage, setHasUserSelectedLanguage] = useState(false);

  useEffect(() => {
    if (languages && languages.length > 0 && selectedLanguageId === 0 && !hasUserSelectedLanguage) {
      const firstLanguage = languages[0];
      if (firstLanguage) {
        setSelectedLanguageId(firstLanguage.id);
      }
    }
  }, [languages, selectedLanguageId, hasUserSelectedLanguage]);

  const page = searchParams.page || 1;
  const pageSize = searchParams.page_size || 25;

  const queryParams: TranslationQueryParams = useMemo(
    () => ({
      language_id: selectedLanguageId,
      page,
      page_size: pageSize,
      search: debouncedSearch || undefined,
      new_only: newOnly || undefined,
    }),
    [selectedLanguageId, page, pageSize, debouncedSearch, newOnly]
  );

  const { data: translationsData, isLoading: isLoadingTranslations } = useTranslations(queryParams);
  const updateTranslation = useUpdateTranslation();
  const exportTranslations = useExportTranslations();
  const importTranslations = useImportTranslations();
  const clearNewEntries = useClearNewEntries();

  const hasAdminPermission = user ? hasFeature(user, 'translation.admin') : false;

  const updateSearchParams = useCallback(
    (updates: Partial<TranslationsSearchParams>) => {
      const newParams: TranslationsSearchParams = { ...searchParams, ...updates };
      setSearchParams(newParams);
    },
    [searchParams]
  );

  useEffect(() => {
    if ((debouncedSearch || '') !== (searchParams.search || '')) {
      updateSearchParams({
        search: debouncedSearch || undefined,
        page: 1,
      });
    }
  }, [debouncedSearch, searchParams.search, updateSearchParams]);

  useEffect(() => {
    if (selectedLanguageId > 0 && selectedLanguageId !== searchParams.language_id) {
      updateSearchParams({ language_id: selectedLanguageId, page: 1 });
    }
  }, [selectedLanguageId, searchParams.language_id, updateSearchParams]);

  useEffect(() => {
    if (newOnly !== (searchParams.new_only || false)) {
      updateSearchParams({ new_only: newOnly || undefined, page: 1 });
    }
  }, [newOnly, searchParams.new_only, updateSearchParams]);

  // Stale checkmarks don't persist when query context changes.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedLanguageId, page, newOnly, debouncedSearch]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: newPage });
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ page_size: newPageSize, page: 1 });
    },
    [updateSearchParams]
  );

  const handleLanguageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLanguageId(Number(e.target.value));
    setHasUserSelectedLanguage(true);
  }, []);

  const handleSaveTranslation = useCallback(
    async (translationId: string, value: string | null) => {
      await updateTranslation.mutateAsync({
        translationId,
        languageId: selectedLanguageId,
        data: { value },
      });
    },
    [updateTranslation, selectedLanguageId]
  );

  const handleMarkReviewed = useCallback(async () => {
    await clearNewEntries.mutateAsync({
      languageId: selectedLanguageId,
      ids: Array.from(selectedIds),
    });
    setSelectedIds(new Set());
  }, [clearNewEntries, selectedLanguageId, selectedIds]);

  const handleExport = useCallback(async () => {
    await exportTranslations.mutateAsync(selectedLanguageId);
  }, [exportTranslations, selectedLanguageId]);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImportError(null);

      try {
        const result = await importTranslations.mutateAsync({
          languageId: selectedLanguageId,
          file,
        });
        setImportResult(result);
        setIsImportModalOpen(true);
      } catch (error) {
        console.error('Import failed:', error);
        const errorMessage = error instanceof Error ? error.message : t('translations.import.failedMessage');
        setImportError(errorMessage);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [importTranslations, selectedLanguageId, t]
  );

  const translations = translationsData?.data || [];
  const total = translationsData?.total || 0;

  return (
    <PageWithHeadline headlineKey="translations.title">
      <ErrorBanner hasError={!!importError}>
        {importError}
      </ErrorBanner>

      <PageContentCard className="flex-1 min-h-0 flex flex-col w-full">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="w-48">
            <SelectDropdown
              value={selectedLanguageId}
              onChange={handleLanguageChange}
              disabled={isLoadingLanguages}
            >
              {isLoadingLanguages ? (
                <option value={0}>{t('translations.toolbar.loadingLanguages')}</option>
              ) : (
                languages?.map((lang) => (
                  <option key={lang.id} value={lang.id}>
                    {lang.name} ({lang.iso_code})
                  </option>
                ))
              )}
            </SelectDropdown>
          </div>

          <div className="flex-1 min-w-[200px] max-w-md">
            <SearchBox
              value={searchInput}
              onChange={setSearchInput}
              placeholder={t('translations.toolbar.searchPlaceholder')}
              ariaLabel={t('translations.toolbar.searchLabel')}
            />
          </div>

          <div className="flex items-center gap-2">
            <Toggle
              checked={newOnly}
              onChange={setNewOnly}
              ariaLabel={t('translations.toolbar.newOnlyLabel')}
            />
            <span className="text-sm text-onsurface">
              {t('translations.toolbar.newOnly')}
            </span>
          </div>

          {hasAdminPermission && (
            <div className="flex items-center gap-2 ml-auto">
              <PrimaryButton
                onClick={handleExport}
                isLoading={exportTranslations.isPending}
                size="sm"
              >
                {t('translations.toolbar.export')}
              </PrimaryButton>

              <PrimaryButton
                onClick={handleImportClick}
                isLoading={importTranslations.isPending}
                size="sm"
              >
                {t('translations.toolbar.import')}
              </PrimaryButton>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          )}
        </div>

        <div className="relative grow min-h-0">
          <TranslationsTable
            translations={translations}
            isLoading={isLoadingTranslations}
            onSave={handleSaveTranslation}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </PageContentCard>

      <FloatingActionBar selectedCount={selectedIds.size}>
        <FloatingActionButton
          onClick={handleMarkReviewed}
          icon={<Check className="size-4" />}
          labelKey="translations.actions.markReviewed"
          isLoading={clearNewEntries.isPending}
        />
      </FloatingActionBar>

      <ImportResultModal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportError(null);
        }}
        result={importResult}
      />
    </PageWithHeadline>
  );
}
